import { Box, Typography } from "@mui/material";
import { Dropdown } from "components";
import { Notice } from "components/Notice";
import { AnimatePresence, motion } from "framer-motion";
import { Control } from "react-hook-form";
import { ContainmentHoldDTO } from "models/dto/containmentHold";
import { ContainmentTypeConfig } from "../hooks/containmentTypeConfig";
import * as labels from "./constants";
import { CardShell, ReadonlyField, SectionHeader } from "./shared";
import { HoldTypeSection } from "./HoldTypeSection";

interface containmentTypeChoice {
	value: string;
	label: string;
}

interface containmentTypeFieldNames {
	initialHold: string;
	subsequentHold: string;
}

const defaultFieldNames: containmentTypeFieldNames = {
	initialHold: "containmentInitialHold",
	subsequentHold: "containmentSubsequentHold",
};

interface containmentTypeCardProps {
	readonly?: boolean;
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	control?: Control<any>;
	containmentType?: string | null;
	containmentTypeLabel?: string | null;
	containmentTypeOptions?: containmentTypeChoice[];
	typeConfig?: ContainmentTypeConfig;
	initialHoldName?: string | null;
	subsequentHoldName?: string | null;
	onContainmentTypeChange?: (value: string) => void;
	getHoldOptionDisabled?: (option: ContainmentHoldDTO) => boolean;
	handleSubsequentHoldChange?: (args: { value: ContainmentHoldDTO | null }) => void;
	handleSubsequentDataLoad?: (options: ContainmentHoldDTO[]) => void;
	onInitialDataLoad?: (data: ContainmentHoldDTO[]) => void;
	autoLoadSubsequentHold?: boolean;
	disabledFields?: {
		containmentType?: boolean;
		initialHold?: boolean;
		subsequentHold?: boolean;
	};
	fieldNames?: Partial<containmentTypeFieldNames>;
	variant?: "default" | "nested";
}

export function ContainmentTypeCard({
	readonly = false,
	control,
	containmentType,
	containmentTypeLabel,
	containmentTypeOptions = [],
	typeConfig,
	initialHoldName,
	subsequentHoldName,
	onContainmentTypeChange,
	getHoldOptionDisabled,
	handleSubsequentHoldChange,
	handleSubsequentDataLoad,
	onInitialDataLoad,
	autoLoadSubsequentHold,
	disabledFields,
	fieldNames: fieldNameOverrides,
	variant = "default",
}: containmentTypeCardProps): React.JSX.Element {
	const fields = { ...defaultFieldNames, ...fieldNameOverrides };

	const hasHolds = typeConfig?.hasHolds ?? false;
	const hasReadonlyContent = containmentTypeLabel || initialHoldName || subsequentHoldName || typeConfig;
	if (readonly && !hasReadonlyContent) return <></>;

	const cardLabel = readonly ? labels.ContainmentSourceSystem : labels.ContainmentType;

	return (
		<CardShell readonly={readonly} label={cardLabel} toolTip="Select the containment type to determine the workflow" variant={variant}>
			{readonly ? (
				<>
					{containmentTypeLabel && (
						<ReadonlyField
							label={labels.ContainmentSourceSystem}
							value={containmentTypeLabel}
							testId="containment-field-containment-type"
						/>
					)}
					{(initialHoldName || subsequentHoldName) && (
						<Box sx={{ pl: 1.5 }}>
							<SectionHeader>{labels.HoldType}</SectionHeader>
							{initialHoldName && (
								<ReadonlyField
									label={labels.OnInitialLoad}
									value={initialHoldName}
									testId="containment-field-initial-hold"
								/>
							)}
							{subsequentHoldName && (
								<ReadonlyField
									label={labels.OnSubsequentScan}
									value={subsequentHoldName}
									testId="containment-field-subsequent-hold"
								/>
							)}
						</Box>
					)}
				</>
			) : (
				<>
					<Box data-test-selector="containment-type-dropdown">
						<Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.5 }}>
							{labels.ContainmentType}
						</Typography>
						<Dropdown
							data={containmentTypeOptions}
							value={containmentTypeOptions.find((o) => o.value === containmentType) ?? null}
							getOptionLabel={(opt) => opt.label}
							isOptionEqualToValue={(a, b) => a.value === b.value}
							onChange={(val) => {
								if (val?.value && onContainmentTypeChange) {
									onContainmentTypeChange(val.value);
								}
							}}
							fullWidth
							multiple={false}
							label={labels.ContainmentType}
							hideLabel
							textFieldProps={{ placeholder: "Select containment type" }}
							clearable={false}
							disabled={disabledFields?.containmentType}
						/>
					</Box>

					<AnimatePresence mode="sync">
						{hasHolds && control && (
							<motion.div
								key="hold-type-section"
								initial={{ opacity: 0, height: 0 }}
								animate={{ opacity: 1, height: "auto" }}
								exit={{ opacity: 0, height: 0 }}
								transition={{ duration: 0.3 }}
								style={{ overflow: "hidden" }}
							>
								<HoldTypeSection
									control={control}
									initialKey={fields.initialHold}
									subsequentKey={fields.subsequentHold}
									getHoldOptionDisabled={getHoldOptionDisabled}
									handleSubsequentHoldChange={handleSubsequentHoldChange}
									handleSubsequentDataLoad={handleSubsequentDataLoad}
									onInitialDataLoad={onInitialDataLoad}
									autoLoadSubsequentHold={autoLoadSubsequentHold}
									initialDisabled={disabledFields?.initialHold}
								/>
							</motion.div>
						)}
					</AnimatePresence>

					{typeConfig && !typeConfig.hasHolds && (
						<Notice
							message="ASRS containments do not require hold configuration. Sector is automatically set to RESS."
							delay={0}
						/>
					)}
				</>
			)}
		</CardShell>
	);
}
